import { useState } from 'react'
import { Button, Input, Label, TextArea, TextField } from '@heroui/react'
import { api } from '../../lib/api'
import { explain } from '../../lib/staffErrors'
import type { ReportView } from '../../lib/types'
import { useStaffText } from '../../ui/StaffText'
import { Notice, ReportModal } from '../kit'
import { EMPTY_SPEC } from './layout'

export const NAME_MAX = 60
export const ABOUT_MAX = 500

/** New report or Edit report: a name and what it is for. New saves at once with an empty layout. */
export function DetailsDialog({
  report,
  organisation,
  onClose,
  onSaved,
}: {
  /** The report being edited; null for a new one. */
  report: ReportView | null
  organisation?: string
  onClose: () => void
  onSaved: (view: ReportView) => void
}) {
  const { lang, say } = useStaffText()
  const [name, setName] = useState(report?.name ?? '')
  const [about, setAbout] = useState(report?.description ?? '')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError(say('pivot.nameNeeded'))
      return
    }
    setBusy(true)
    setError('')
    try {
      const { view } = report
        ? await api.updateReportView(report.id, { name: trimmed, shared: report.shared, description: about.trim(), updatedAt: report.updatedAt })
        : await api.saveReportView({ name: trimmed, kind: 'pivot', shared: true, description: about.trim(), spec: { period: '', pivot: EMPTY_SPEC }, organisation })
      onSaved(view)
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
      setBusy(false)
    }
  }

  return (
    <ReportModal
      title={say(report ? 'pivot.editReport' : 'pivot.newReport')}
      onClose={onClose}
      onSubmit={() => void submit()}
      footer={(form) => (
        <>
          <Button variant="tertiary" size="sm" onPress={onClose}>
            {say('common.cancel')}
          </Button>
          <Button type="submit" form={form} variant="primary" size="sm" isDisabled={busy}>
            {say(report ? 'common.save' : 'pivot.continue')}
          </Button>
        </>
      )}
    >
      {error && <Notice>{error}</Notice>}
      <TextField
        autoFocus
        value={name}
        maxLength={NAME_MAX}
        isInvalid={!!error && !name.trim()}
        onChange={(v) => {
          setName(v)
          setError('')
        }}
      >
        <Label>{say('pivot.name')}</Label>
        <Input />
      </TextField>
      <TextField value={about} maxLength={ABOUT_MAX} onChange={setAbout}>
        <Label>{say('pivot.description')}</Label>
        <TextArea rows={3} />
      </TextField>
    </ReportModal>
  )
}

/** Asks before a report and its schedules go. */
export function DeleteDialog({ report, onClose, onDeleted }: { report: ReportView; onClose: () => void; onDeleted: () => void }) {
  const { lang, say } = useStaffText()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const remove = async () => {
    setBusy(true)
    try {
      await api.deleteReportView(report.id)
      onDeleted()
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
      setBusy(false)
    }
  }
  return (
    <ReportModal
      title={say('pivot.deleteAsk', { name: report.name })}
      size="sm"
      onClose={onClose}
      footer={() => (
        <>
          <Button variant="tertiary" size="sm" onPress={onClose}>
            {say('common.cancel')}
          </Button>
          <Button variant="danger" size="sm" isDisabled={busy} onPress={() => void remove()}>
            {say('common.delete')}
          </Button>
        </>
      )}
    >
      {error ? <Notice>{error}</Notice> : <p className="rp-muted">{say('pivot.deleteNote')}</p>}
    </ReportModal>
  )
}

/** "Leave without saving?" for a draft report. */
export function LeaveDialog({ onStay, onLeave }: { onStay: () => void; onLeave: () => void }) {
  const { say } = useStaffText()
  return (
    <ReportModal
      title={say('pivot.leaveAsk')}
      size="sm"
      onClose={onStay}
      footer={() => (
        <>
          <Button variant="tertiary" size="sm" onPress={onStay}>
            {say('pivot.stay')}
          </Button>
          <Button variant="danger" size="sm" onPress={onLeave}>
            {say('pivot.leave')}
          </Button>
        </>
      )}
    >
      <p className="rp-muted">{say('pivot.leaveNote')}</p>
    </ReportModal>
  )
}
